import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/ProductionSteps_SalvarInput.ts';
import '../contracts/ProductionSteps_RemoverInput.ts';
import '../contracts/ProductionSteps_SaveServiceStepsInput.ts';
import '../contracts/ProductionSteps_SaveAdditionalServiceStepsInput.ts';

import '../contracts/ProductionSteps_AdditionalServiceStepsResponse';
import '../contracts/ProductionSteps_GetResponse';
import '../contracts/ProductionSteps_KvResponse';
import '../contracts/ProductionSteps_ListaResponse';
import '../contracts/ProductionSteps_RemoverResponse';
import '../contracts/ProductionSteps_SalvarResponse';
import '../contracts/ProductionSteps_SaveAdditionalServiceStepsResponse';
import '../contracts/ProductionSteps_SaveServiceStepsResponse';
import '../contracts/ProductionSteps_ServiceStepsResponse';
import type { ProductionSteps_RemoverInput } from '../contracts/ProductionSteps_RemoverInput';
import type { ProductionSteps_SalvarInput } from '../contracts/ProductionSteps_SalvarInput';
import type { ProductionSteps_SaveAdditionalServiceStepsInput } from '../contracts/ProductionSteps_SaveAdditionalServiceStepsInput';
import type { ProductionSteps_SaveServiceStepsInput } from '../contracts/ProductionSteps_SaveServiceStepsInput';
/**
 * Composable for SO Production Steps operations.
 */
export function useSOProductionSteps() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/production_steps';

  const fetchAll = async () => {
    log('useSOProductionSteps', 'fetchAll', 'Fetching all production steps');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'ProductionSteps_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar etapas de producao');
    } catch (err) {
      logError('useSOProductionSteps', 'fetchAll', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getById = async (id: any) => {
    log('useSOProductionSteps', 'getById', 'Fetching production step', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/get/${id}`, { responseContract: 'ProductionSteps_GetResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar etapa de producao');
    } catch (err) {
      logError('useSOProductionSteps', 'getById', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: ProductionSteps_SalvarInput) => {
    log('useSOProductionSteps', 'save', 'Saving production step', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('ProductionSteps_SalvarInput', data);
      const response = await httpService.post(`${basePath}/salvar`, data, { responseContract: 'ProductionSteps_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar etapa de producao');
    } catch (err) {
      logError('useSOProductionSteps', 'save', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (data: ProductionSteps_RemoverInput) => {
    log('useSOProductionSteps', 'remove', 'Removing production step', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('ProductionSteps_RemoverInput', data);
      const response = await httpService.post(`${basePath}/remover`, data, { responseContract: 'ProductionSteps_RemoverResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover etapa de producao');
    } catch (err) {
      logError('useSOProductionSteps', 'remove', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchKV = async () => {
    log('useSOProductionSteps', 'fetchKV', 'Fetching KV for production steps');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/kv`, { responseContract: 'ProductionSteps_KvResponse' });
      if (response.success) {
        return response.dados || {};
      }
      throw new Error(response.error || 'Erro ao buscar KV de etapas de producao');
    } catch (err) {
      logError('useSOProductionSteps', 'fetchKV', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const saveServiceSteps = async (serviceId: any, steps: any) => {
    log('useSOProductionSteps', 'saveServiceSteps', 'Saving service steps', { serviceId, steps });
    isLoading.value = true;
    error.value = null;
    try {
      const payload: ProductionSteps_SaveServiceStepsInput = { service_id: serviceId, steps };
      validate('ProductionSteps_SaveServiceStepsInput', payload);
      const response = await httpService.post(`${basePath}/save_service_steps`, payload, { responseContract: 'ProductionSteps_SaveServiceStepsResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar etapas do servico');
    } catch (err) {
      logError('useSOProductionSteps', 'saveServiceSteps', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const saveAdditionalServiceSteps = async (additionalServiceId: any, steps: any) => {
    log('useSOProductionSteps', 'saveAdditionalServiceSteps', 'Saving additional service steps', { additionalServiceId, steps });
    isLoading.value = true;
    error.value = null;
    try {
      const payload: ProductionSteps_SaveAdditionalServiceStepsInput = { additional_service_id: additionalServiceId, steps };
      validate('ProductionSteps_SaveAdditionalServiceStepsInput', payload);
      const response = await httpService.post(`${basePath}/save_additional_service_steps`, payload, { responseContract: 'ProductionSteps_SaveAdditionalServiceStepsResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar etapas do servico adicional');
    } catch (err) {
      logError('useSOProductionSteps', 'saveAdditionalServiceSteps', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchServiceSteps = async (serviceId: number) => {
    log('useSOProductionSteps', 'fetchServiceSteps', 'Fetching service steps', { serviceId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/service_steps/${serviceId}`, { responseContract: 'ProductionSteps_ServiceStepsResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar etapas do servico');
    } catch (err) {
      logError('useSOProductionSteps', 'fetchServiceSteps', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAdditionalServiceSteps = async (additionalServiceId: number) => {
    log('useSOProductionSteps', 'fetchAdditionalServiceSteps', 'Fetching additional service steps', { additionalServiceId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/additional_service_steps/${additionalServiceId}`, { responseContract: 'ProductionSteps_AdditionalServiceStepsResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar etapas do servico adicional');
    } catch (err) {
      logError('useSOProductionSteps', 'fetchAdditionalServiceSteps', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchAll,
    getById,
    save,
    remove,
    fetchKV,
    saveServiceSteps,
    saveAdditionalServiceSteps,
    fetchServiceSteps,
    fetchAdditionalServiceSteps
  };
}
